import { MessageNotificationDto, MessageNotificationTypeEnum } from '@/services/api-clients';

const STORAGE_KEY = 'supportTicketsInView';
const HEARTBEAT_MS = 20000;
const STALE_AFTER_MS = 60000;

type ViewSource = 'page' | 'popup';

interface TabEntry {
  ids: number[];
  at: number;
}

// Which support ticket each open portal tab shows right now (the ticket page or the chat popup),
// shared through localStorage so a driver's message in a ticket on screen does not toast in any tab.
class SupportTicketViewRegistry {
  private readonly tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  private shown: Record<ViewSource, number | null> = { page: null, popup: null };

  private heartbeatId: number | null = null;

  constructor() {
    window.addEventListener('beforeunload', () => this.write(null));
  }

  setShown(source: ViewSource, ticketId: number | null) {
    this.shown[source] = ticketId;
    const ids = this.ownIds();
    this.write(ids.length ? { ids, at: Date.now() } : null);
    if (ids.length && this.heartbeatId === null) {
      this.heartbeatId = window.setInterval(() => this.write({ ids: this.ownIds(), at: Date.now() }), HEARTBEAT_MS);
    }
    if (!ids.length && this.heartbeatId !== null) {
      window.clearInterval(this.heartbeatId);
      this.heartbeatId = null;
    }
  }

  isShownHere(ticketId: number): boolean {
    return this.ownIds().includes(ticketId);
  }

  isShownAnywhere(ticketId: number): boolean {
    const now = Date.now();
    return Object.values(this.readAll()).some((e) => now - e.at < STALE_AFTER_MS && e.ids.includes(ticketId));
  }

  // The ticket a driver's message notification is about, when it is a driver's message.
  driverMessageTicketId(notification: MessageNotificationDto): number | null {
    if (notification.type !== MessageNotificationTypeEnum.SupportTicketDriverMessage) return null;
    try {
      return (JSON.parse(notification.model) as { ticketId?: number }).ticketId ?? null;
    } catch {
      return null;
    }
  }

  private ownIds(): number[] {
    return [...new Set(Object.values(this.shown).filter((id): id is number => id !== null))];
  }

  private readAll(): Record<string, TabEntry> {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    } catch {
      return {};
    }
  }

  private write(entry: TabEntry | null) {
    const all = this.readAll();
    const now = Date.now();
    Object.keys(all).forEach((key) => { if (now - all[key].at >= STALE_AFTER_MS) delete all[key]; });
    if (entry) all[this.tabId] = entry;
    else delete all[this.tabId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  }
}

export const supportTicketViewRegistry = new SupportTicketViewRegistry();
